/**
 * Top-5 Marken (Anfragen-Routing) — Jun-2026 (User-Wunsch): aus der Werkstatt-
 * Sektion in die Marktplatzeinstellungen verschoben. Eigenständig mit
 * Autospeichern (PATCH dealer-profile favorite_brands). Bei einer Filiale, deren
 * Hauptsitz „top_marken" org-weit erzwingt, read-only.
 */
import { useEffect, useState, useCallback } from "react";
import { Star, Lock, X } from "lucide-react";
import { Input } from "../ui/input";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import BranchOverrideToggle from "./BranchOverrideToggle";
import { useAutosave } from "../../hooks/useAutosave";
import SaveStatusIndicator from "./SaveStatusIndicator";

export default function FavoriteBrandsPanel({ currentUser, locked = false }) {
    const { refresh } = useAuth();
    const [brands, setBrands] = useState([]);
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState(currentUser.favorite_brands || []);
    const isHauptsitz = currentUser.branch_role === "hauptsitz";

    useEffect(() => {
        api.get("/brands").then((r) => setBrands(r.data?.all || [])).catch(() => {});
    }, []);

    const autosave = useCallback(async (val) => {
        await api.patch("/users/me/dealer-profile", { favorite_brands: val });
        await refresh();
    }, [refresh]);
    const sig = useAutosave(selected, autosave, { delay: 800 });

    const toggle = (b) => {
        if (locked) return;
        setSelected((prev) =>
            prev.includes(b) ? prev.filter((x) => x !== b) : (prev.length >= 10 ? prev : [...prev, b])
        );
    };
    const filtered = brands.filter((b) => b.toLowerCase().includes(filter.toLowerCase()));

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="top5-brands-panel">
            <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-[#FFCC00]" />
                <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700">Top-10 Marken (Routing eingehender Anfragen)</h4>
                <div className="ml-auto flex items-center gap-2">
                    {!locked && <SaveStatusIndicator saving={sig.saving} dirty={sig.dirty} savedAt={sig.savedAt} />}
                    {isHauptsitz ? (
                        <BranchOverrideToggle
                            area="top_marken"
                            currentUser={currentUser}
                            infoText="Top-5 Marken (Anfragen-Routing) des Hauptsitzes auf alle Filialen übernehmen & überschreiben."
                            testId="top-marken-branch-override"
                        />
                    ) : locked ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm" data-testid="top-marken-locked-badge">
                            <Lock className="w-3 h-3" /> Vom Hauptsitz
                        </span>
                    ) : null}
                </div>
            </div>
            <p className="text-xs text-gray-600 mb-2">Eingehende Käufer-Anfragen für diese Marken werden Ihnen bevorzugt zugeordnet.</p>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2" data-testid="fav-brand-selected-badges">
                    {selected.map((b) => (
                        <span key={b} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#111827] text-white text-[11px] font-bold" data-testid={`fav-brand-badge-${b}`}>
                            {b}
                            {!locked && (
                                <button type="button" onClick={() => toggle(b)} className="hover:text-[#FFCC00]" aria-label={`${b} entfernen`} data-testid={`fav-brand-badge-remove-${b}`}>
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            <fieldset disabled={locked} className="border-0 p-0 m-0 disabled:opacity-60">
                <Input
                    placeholder="Marke suchen…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="rounded-sm mb-2 h-8 text-sm bg-white"
                    data-testid="fav-brand-filter"
                />
                <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                    {filtered.map((b) => (
                        <button
                            key={b}
                            type="button"
                            onClick={() => toggle(b)}
                            className={`px-2 py-1 text-xs rounded-sm border ${selected.includes(b) ? "bg-[#111827] border-[#111827] text-white font-bold" : "bg-white border-gray-300 text-gray-700 hover:border-[#111827]"}`}
                            data-testid={`fav-brand-${b}`}
                        >
                            {b}
                        </button>
                    ))}
                </div>
                <div className="mt-2 text-[11px] text-gray-500">{selected.length}/10 ausgewählt</div>
            </fieldset>
        </div>
    );
}
